import React, { createContext, useState, useContext } from 'react';
import type { ReactNode } from 'react';

import type { GenericImportedJobType } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration-select';

import type { SDBJobs } from 'state/__generated__/tadaahAPI';

export type IntegrationSettingsContextType = {
  create: {
    createdJobIds: string[];
    setCreatedJobIds: React.Dispatch<React.SetStateAction<string[]>>;
  };
  fetch: {
    fetchedJobs: SDBJobs[];
    setFetchedJobs: React.Dispatch<React.SetStateAction<SDBJobs[]>>;
    hasFetchedSdbJobs: boolean;
    setHasFetchedSdbJobs: React.Dispatch<React.SetStateAction<boolean>>;
  };
  select: {
    selectedJobs: GenericImportedJobType[];
    setSelectedJobs: React.Dispatch<
      React.SetStateAction<GenericImportedJobType[]>
    >;
  };
  settings: {
    hasClickedUpdateApiKey: boolean;
    setHasClickedUpdateApiKey: React.Dispatch<React.SetStateAction<boolean>>;
    apiKeyFieldType: 'text' | 'password';
    setApiKeyFieldType: React.Dispatch<
      React.SetStateAction<'text' | 'password'>
    >;
  };
  table: {
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    currentPageSize: number;
    setCurrentPageSize: React.Dispatch<React.SetStateAction<number>>;
    selectedJobIds: (string | null | undefined)[];
    setSelectedJobIds: React.Dispatch<
      React.SetStateAction<(string | null | undefined)[]>
    >;
  };
};

export const integrationSettingsInitialValue: IntegrationSettingsContextType = {
  create: {
    createdJobIds: [],
    setCreatedJobIds: () => {},
  },
  fetch: {
    fetchedJobs: [],
    hasFetchedSdbJobs: false,
    setFetchedJobs: () => {},
    setHasFetchedSdbJobs: () => {},
  },
  select: {
    selectedJobs: [],
    setSelectedJobs: () => {},
  },
  settings: {
    apiKeyFieldType: 'password',
    hasClickedUpdateApiKey: false,
    setApiKeyFieldType: () => {},
    setHasClickedUpdateApiKey: () => {},
  },
  table: {
    currentPageSize: 50,
    page: 1,
    selectedJobIds: [],
    setCurrentPageSize: () => {},
    setPage: () => {},
    setSelectedJobIds: () => {},
  },
};

const IntegrationSettingsCtx = createContext<IntegrationSettingsContextType>(
  integrationSettingsInitialValue,
);

export const IntegrationSettingsContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [hasFetchedSdbJobs, setHasFetchedSdbJobs] = useState<boolean>(false);
  const [hasClickedUpdateApiKey, setHasClickedUpdateApiKey] =
    useState<boolean>(false);
  const [apiKeyFieldType, setApiKeyFieldType] = useState<'password' | 'text'>(
    'password',
  );

  const [fetchedJobs, setFetchedJobs] = useState<SDBJobs[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<GenericImportedJobType[]>(
    [],
  );
  const [createdJobIds, setCreatedJobIds] = useState<string[]>([]);

  const [page, setPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [selectedJobIds, setSelectedJobIds] = useState<
    (string | null | undefined)[]
  >([]);

  const integrationSettingsContext: IntegrationSettingsContextType = {
    create: {
      createdJobIds,
      setCreatedJobIds,
    },
    fetch: {
      fetchedJobs,
      hasFetchedSdbJobs,
      setFetchedJobs,
      setHasFetchedSdbJobs,
    },
    select: {
      selectedJobs,
      setSelectedJobs,
    },
    settings: {
      apiKeyFieldType,
      hasClickedUpdateApiKey,
      setApiKeyFieldType,
      setHasClickedUpdateApiKey,
    },
    table: {
      currentPageSize,
      page,
      selectedJobIds,
      setCurrentPageSize,
      setPage,
      setSelectedJobIds,
    },
  };

  return (
    <IntegrationSettingsCtx.Provider value={integrationSettingsContext}>
      {children}
    </IntegrationSettingsCtx.Provider>
  );
};

export const useIntegrationSettingsContext =
  (): IntegrationSettingsContextType => useContext(IntegrationSettingsCtx);

export default {
  Consumer: IntegrationSettingsCtx.Consumer,
  Provider: IntegrationSettingsContextProvider,
};
